/**
 * Calendario laboral: festivos por calendario (nacional/autonómico/local/convenio).
 *
 * Un convenio puede mover un festivo local o de convenio de fecha (p. ej. el
 * "festivo sustituible" que cae en domingo se traslada) — por eso editar la
 * FECHA de un festivo ya existente es un caso de uso de primera clase, no un
 * detalle. El sembrado (`seedCalendar`) es idempotente: solo añade nacionales
 * y, si la región es CAT o MAD, sus autonómicos; el resto (locales, convenio)
 * se dan de alta a mano aquí. Activar un calendario desactiva los demás del
 * mismo año en el backend, así que tras activar se recarga la lista entera
 * en vez de tocar el estado local a mano.
 *
 * Este fichero es el orquestador: mantiene el estado de datos (calendarios,
 * festivos, selección, error/aviso) y las llamadas a la API; compone
 * `CalendarList` (panel izquierdo: lista + alta + acciones por fila) y
 * `HolidayPanel` (panel derecho: festivos del calendario seleccionado).
 * Ambos comparten el mismo grid de dos columnas — ver el comentario de
 * `CalendarList` sobre por qué su botón y su formulario de alta llevan
 * `lg:col-span-2`.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { CalendarList } from './calendar/CalendarList';
import { HolidayPanel } from './calendar/HolidayPanel';
import {
  getCalendars,
  getCalendarHolidays,
  createCalendar,
  seedCalendar,
  addHoliday,
  updateHoliday,
  deleteHoliday,
  setCalendarActive,
  deleteCalendar,
  WorkCalendarSummary,
  CalendarHolidayItem,
  apiMessage,
} from '../../services/apiClient';

export function CalendarAdmin() {
  const [calendars, setCalendars] = useState<WorkCalendarSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [holidays, setHolidays] = useState<CalendarHolidayItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selected = calendars.find((c) => c.id === selectedId) ?? null;

  const clearError = () => setError(null);
  const clearMessages = () => {
    setError(null);
    setNotice(null);
  };

  const load = useCallback(async (preferId?: number) => {
    const list = await getCalendars();
    setCalendars(list);
    const preferred = preferId != null ? list.find((c) => c.id === preferId) : undefined;
    setSelectedId((prev) => {
      const keepPrev = prev != null && list.some((c) => c.id === prev);
      return preferred?.id ?? (keepPrev ? prev : list[0]?.id ?? null);
    });
    setLoaded(true);
  }, []);

  useEffect(() => {
    load().catch(() => {
      setError('No se pudieron cargar los calendarios');
      setLoaded(true);
    });
  }, [load]);

  const loadHolidays = useCallback(async (calId: number) => {
    setLoadingHolidays(true);
    try {
      setHolidays(await getCalendarHolidays(calId));
    } catch {
      setError('No se pudieron cargar los festivos');
      setHolidays([]);
    } finally {
      setLoadingHolidays(false);
    }
  }, []);

  useEffect(() => {
    // Un mensaje de la fila anterior (error o aviso de sembrado) no debe
    // sobrevivir al cambio de calendario seleccionado. El formulario de alta
    // de festivo y la edición inline viven en HolidayPanel, montado con
    // `key={selectedId}` más abajo — cambiar de selección ya los resetea.
    setError(null);
    setNotice(null);
    if (selectedId != null) {
      loadHolidays(selectedId);
    } else {
      setHolidays([]);
    }
  }, [selectedId, loadHolidays]);

  const handleCreateCalendar = async (data: { name: string; year: number; region: string | null }) => {
    setError(null);
    setNotice(null);
    try {
      const created = await createCalendar(data);
      // Sembrado automático justo tras crear: es idempotente, así que no hay
      // riesgo de duplicar si el admin vuelve a pulsar "Sembrar" después. Si
      // falla, el calendario ya existe (no es un error duro): se avisa para
      // que el admin lo sepa y sembre a mano, en vez de fallar en silencio.
      let seedFailed = false;
      try {
        await seedCalendar(created.id);
      } catch {
        seedFailed = true;
      }
      // Se recarga y selecciona el nuevo calendario antes de fijar el aviso:
      // el cambio de selectedId limpia error/notice (ver el efecto de
      // arriba), así que el aviso tiene que llegar después, no antes.
      await load(created.id);
      if (seedFailed) {
        setNotice('Calendario creado, pero el sembrado automático falló. Usa el botón "Sembrar".');
      }
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo crear el calendario'));
      throw e;
    }
  };

  const handleSeed = async (calId: number) => {
    setError(null);
    try {
      await seedCalendar(calId);
      await loadHolidays(calId);
      await load(calId);
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo sembrar el calendario'));
    }
  };

  const handleActivate = async (calId: number) => {
    setError(null);
    try {
      await setCalendarActive(calId, true);
      // Activar uno desactiva los demás del mismo año en el backend: se
      // recarga la lista entera para reflejarlo, no se parchea a mano.
      await load(calId);
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo activar el calendario'));
    }
  };

  const handleDeleteCalendar = async (calId: number) => {
    setError(null);
    try {
      await deleteCalendar(calId);
      if (selectedId === calId) setSelectedId(null);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo borrar el calendario'));
    }
  };

  // Tipo del alta manual de festivo: LOCAL por defecto y COMPANY (de
  // convenio) como alternativa — `HolidayCreate.holiday_type` es texto libre
  // (max 20) en el backend, pero nacionales y autonómicos vienen del
  // SEMBRADO: darlos a mano aquí duplicaría con el seed. Locales y de
  // convenio "se dan de alta a mano aquí", como promete el encabezado —
  // hasta ahora el alta hardcodeaba LOCAL y un festivo de convenio nacía
  // mal etiquetado (ronda 4, r2b20[5]).
  const handleAddHoliday = async (data: { date: string; name: string; holiday_type: 'LOCAL' | 'COMPANY' }) => {
    if (selectedId == null) return;
    setError(null);
    try {
      await addHoliday(selectedId, {
        date: data.date,
        name: data.name,
        holiday_type: data.holiday_type,
      });
      await loadHolidays(selectedId);
      await load(selectedId);
    } catch (e: unknown) {
      // 409: la fecha ya tiene un festivo en este calendario.
      setError(apiMessage(e, 'No se pudo añadir el festivo'));
      throw e;
    }
  };

  const handleEditHoliday = async (holidayId: number, data: { date: string; name: string }) => {
    if (selectedId == null) return;
    setError(null);
    try {
      await updateHoliday(selectedId, holidayId, { date: data.date, name: data.name.trim() });
      await loadHolidays(selectedId);
    } catch (e: unknown) {
      // 409: la fecha destino ya está ocupada por otro festivo del calendario.
      setError(apiMessage(e, 'No se pudo mover el festivo'));
      throw e;
    }
  };

  const handleDeleteHoliday = async (h: CalendarHolidayItem) => {
    if (selectedId == null) return;
    setError(null);
    try {
      await deleteHoliday(selectedId, h.id);
      await loadHolidays(selectedId);
      await load(selectedId);
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo borrar el festivo'));
    }
  };

  if (!loaded) {
    return (
      <AdminLayout title="Calendario">
        {error ? (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] max-w-[560px]">{error}</p>
        ) : (
          <p className="text-ink-3 text-[14px]">Cargando…</p>
        )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Calendario" subtitle={selected?.name}>
      <div className="max-w-[1100px] space-y-4">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}
        {notice && <p className="rounded-xl bg-warn-soft text-warn px-4 py-3 text-[14px]">{notice}</p>}

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-start">
          <CalendarList
            calendars={calendars}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={handleCreateCalendar}
            onActivate={handleActivate}
            onSeed={handleSeed}
            onDelete={handleDeleteCalendar}
            onClearMessages={clearMessages}
          />
          <HolidayPanel
            key={selectedId ?? 'none'}
            selected={selected}
            holidays={holidays}
            loadingHolidays={loadingHolidays}
            onAdd={handleAddHoliday}
            onEdit={handleEditHoliday}
            onDelete={handleDeleteHoliday}
            onClearError={clearError}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
